import { createElement, Plus } from "lucide";
import { twJoin } from "tailwind-merge";
import { getAssetAvailableTypeByExtension } from "~/js/network/mime-type-handler";
import { config } from "~/js/network/network-handler";
import { $div } from "~/js/utilities/TurboDom";
import {
	convertAvifToPng,
	convertHeicToPng,
	getPrivateMessageFromError,
	getPublicMessageFromError,
	lovercaseFileFormat
} from "~/solidJs/shared";
import { addHeightWidthToFile } from "~/solidJs/shared/helpers/validateImages";
import EventAnalytics from "../../../utilities/EventAnalytics";
import { SocketDefinition } from "../../node-definition";

const fileSizeLimitBytes = 1024 * 1024 * 1024;
const isFileWasOpenedKey = "isFileWasOpened";

const applyExperimentStylesInit = (btn) => {
	btn.textContent = "Upload";
	btn.classList.add(twJoin("black-import-files"));
	btn.prepend(createElement(Plus));
};

const getAbTestInitState = (handleFileChange, nodeUUID) => {
	const initContainer = $div("importFilesAbInitBody");

	const clickableArea = document.createElement("label");
	clickableArea.setAttribute("data-test", "openFileBtn");
	initContainer.append(clickableArea);
	clickableArea.htmlFor = `file-input-${nodeUUID}`;
	clickableArea.classList.add("importFilesAbInitClickableArea");
	clickableArea.addEventListener("click", () =>
		EventAnalytics.fileImport.openFilePicker("init-state")
	);
	const textContainer = $div("importFilesAbInitTextContainer", clickableArea);
	$div("importFilesAbInitIcon", textContainer);
	const text = $div("importFilesAbInitText", textContainer);
	text.textContent = "Choose your files\nor drop here";
	const input = document.createElement("input");
	input.id = `file-input-${nodeUUID}`;
	input.type = "file";
	input.multiple = true;
	input.addEventListener("change", (e) => handleFileChange(Array.from(e.target.files)));
	input.hidden = true;
	initContainer.appendChild(input);
	const button = document.createElement("div");
	button.classList.add("importFilesAbInitButton");
	button.textContent = "Upload";
	clickableArea.appendChild(button);
	return initContainer;
};

// Export only the executor function
export const readFileCustomExecutor = (node) => {
	const layout = node.executionGroupLayout;
	const nodeBody = node.layout.DOM.nodeBody;

	const oldFunc = node.updateOutputs.bind(node);

	node.updateOutputs = (files, invokeUpdate) => {
		toggleInitBody(node.outputSocketGroup.sockets.length > 0);

		oldFunc(files, invokeUpdate);

		// Update image viewers with S3 URLs if available
		// This runs after oldFunc creates the viewers
		if (invokeUpdate && files) {
			requestAnimationFrame(() => {
				const imageElements = nodeBody.querySelectorAll(".imageViewer");
				imageElements.forEach((img, index) => {
					const file = files[index];
					if (file?.originalSrc?.startsWith("http")) {
						img.src = file.originalSrc;
					}
				});
			});
		}
	};

	const inputFile = document.createElement("input");
	inputFile.multiple = true;
	inputFile.type = "file";
	inputFile.hidden = true;
	inputFile.id = `input-file${node.uuid}`;
	const inputFileLabel = document.createElement("label");
	inputFileLabel.htmlFor = `input-file${node.uuid}`;
	inputFileLabel.textContent = "Upload files";
	inputFileLabel.setAttribute("data-test", "reopenFileBtn");
	inputFileLabel.classList.add("read-file-input");
	inputFileLabel.addEventListener("click", () =>
		EventAnalytics.fileImport.openFilePicker("button")
	);
	applyExperimentStylesInit(inputFileLabel);

	const dragArea = node.layout.domElement();
	dragArea.addEventListener("dragover", (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!Array.from(e.dataTransfer.types).includes("Files")) return;
		e.stopImmediatePropagation();
		dragArea.classList.add("dragging");
	});

	dragArea.addEventListener("dragleave", (e) => {
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();
		dragArea.classList.remove("dragging");
	});

	layout.appendContent(inputFile);
	layout.appendContent(inputFileLabel);
	const errors = [];
	const showError = (error) => {
		const statusElement = document.createElement("div");
		statusElement.classList.add("status-message");
		statusElement.classList.add("error");
		statusElement.textContent = error;

		errors.push(statusElement);
		layout.appendContent(statusElement);
	};
	const checkFileFormatOnSupport = (file) => {
		const fileFormat = file.name.split(".").pop().toLowerCase();
		const fileType = getAssetAvailableTypeByExtension(fileFormat);
		return fileType;
	};
	dragArea.addEventListener("drop", (e) => {
		if (!Array.from(e.dataTransfer.types).includes("Files")) return;
		EventAnalytics.fileImport.dropFiles();
		e.preventDefault();
		e.stopPropagation();

		dragArea.classList.remove("dragging");

		for (const error of errors) {
			error.remove();
		}
		const droppedFiles = Array.from(e.dataTransfer.files);

		const newTransfer = new DataTransfer();
		for (const file of droppedFiles) {
			const fileType = checkFileFormatOnSupport(file);
			if (!fileType) {
				showError(`"${file.name}" file format is unsupported`);
				return;
			}
			newTransfer.items.add(file);
		}
		inputFile.files = newTransfer.files;
		handleInputFileChange(inputFile.files, true);
	});

	const validateFiles = async (files) => {
		const outputSocketFiles = [];
		for (const file of files) {
			EventAnalytics.tryAddFile();

			if (file.size > fileSizeLimitBytes && config.IS_PRODUCTION === "true") {
				showError(`"${file.name}" file size is above 1 GB limit`);
				continue;
			}

			let processedFile;
			try {
				processedFile = await convertHeicToPng(file);
				processedFile = await convertAvifToPng(processedFile);
				processedFile = lovercaseFileFormat(processedFile);
			} catch (error) {
				showError(error.message);
				continue;
			}

			const fileType = checkFileFormatOnSupport(processedFile);
			if (!fileType) {
				showError(`"${file.name}" file format is unsupported`);
				continue;
			}
			EventAnalytics.addFile();
			const splitName = processedFile.name.split(".");
			const fileName = splitName[0];

			await addHeightWidthToFile(processedFile);

			const socketData = new SocketDefinition(
				fileName,
				fileType,
				{
					value: processedFile
				},
				null,
				fileName
			);

			outputSocketFiles.push(socketData);
		}

		return outputSocketFiles;
	};
	const handleInputFileChange = async (files, isNeedToSkipClearErrors) => {
		try {
			inputFileLabel.classList.remove("purple-import-files");
			localStorage.setItem(isFileWasOpenedKey, true);
			if (!isNeedToSkipClearErrors) {
				for (const error of errors) {
					error.remove();
				}
			}
			const outputSocketFiles = await validateFiles(files);
			if (outputSocketFiles.length > 0) {
				node.outputSocketGroup.setSockets(outputSocketFiles);
				node.outputSocketGroup.updateMultiSockets();

				// Show files immediately with blob URLs for instant feedback
				node.updateOutputs(files);

				// Upload to S3 in background and update when ready
				// This prevents issues with moved/deleted files
				uploadFilesImmediately(files, node).then(() => {
					// Update with S3 URLs after upload completes
					node.updateOutputs(files, true);
				});
			}
		} catch (error) {
			showError(getPublicMessageFromError(error));
			EventAnalytics.sendErrorInfo("error-import-files", {
				message: getPrivateMessageFromError(error)
			});
		}
	};

	/**
	 * Upload files immediately to S3 as temporary files
	 * This prevents issues when files are moved or deleted before saving/running
	 */
	const uploadFilesImmediately = async (files, _node) => {
		try {
			const { FileApi } = await import("~/solidJs/shared/api/fileApi");

			// Upload each file
			for (const file of files) {
				try {
					// Skip if already uploaded with S3 link
					if (
						file.fileId &&
						file.isStoredRemote &&
						file.originalSrc?.startsWith("http")
					) {
						continue;
					}

					// Upload as temporary file and get S3 link
					const { file: updatedFile } = await FileApi.uploadFileImmediately(file);
					console.log(
						`File ${file.name} uploaded to S3 as temporary, new src: ${updatedFile.originalSrc}`
					);

					// Update the file object with new src
					Object.assign(file, updatedFile);
				} catch (error) {
					console.error(`Failed to upload file ${file.name}:`, error);
					// Continue with other files even if one fails
				}
			}
			// Files are now updated with S3 URLs
			// The caller will trigger node.updateOutputs
		} catch (error) {
			console.error("Failed to upload files immediately:", error);
		}
	};
	const deleteFile = (file) => {
		const inputFilesArr = Array.from(inputFile.files).filter((item) => item !== file);
		const newTransfer = new DataTransfer();
		for (const file of inputFilesArr) {
			newTransfer.items.add(file);
		}
		inputFile.files = newTransfer.files;
	};
	const toggleInitBody = (force) => {
		importFilesInitBody.classList.toggle("hidden", force);
		inputFileLabel.classList.toggle("hidden", !force);
		node.outputSocketGroup.layout.domElement().classList.toggle("hidden", !force);
	};
	node.handleInputFileChange = handleInputFileChange;
	node.deleteFile = deleteFile;
	inputFile.addEventListener("change", (e) => handleInputFileChange(Array.from(e.target.files)));
	const importFilesInitBody = getAbTestInitState(handleInputFileChange, node.uuid);
	toggleInitBody(node.outputSocketGroup.sockets.length > 0);
	nodeBody.append(importFilesInitBody);
};
